import { useTranslation } from "react-i18next";
import { ArrowClockwiseIcon, WarningIcon } from "@phosphor-icons/react";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { getErrorConfig } from "@/zapi-frontend/shared/config/errorMessages";

type TAnalysisFailedScreenProps = {
  error: string;
  onRetry: () => void;
  isRetrying?: boolean;
};

export function AnalysisFailedScreen({ error, onRetry, isRetrying }: TAnalysisFailedScreenProps) {
  const { t } = useTranslation("projectHome");
  const { t: tCommon } = useTranslation("common");
  const { isLabs } = useMeStore();

  const { errorKey } = getErrorConfig(error);

  const { title, description } =
    errorKey === "RATE_LIMIT"
      ? { title: t("generation.failedTitleRateLimit"), description: t("generation.failedDescriptionRateLimit") }
      : errorKey === "TIMEOUT"
      ? { title: t("generation.failedTitleTimeout"), description: t("generation.failedDescriptionTimeout") }
      : { title: t("generation.failedTitleGeneric"), description: t("generation.failedDescriptionGeneric") };

  return (
    <div className="w-full h-full flex items-center justify-center">
      <div data-testid="analysis-failed" className="flex flex-col items-center gap-4 px-6 mt-[-40px]">
        <div className="flex items-center justify-center size-11 rounded-full border border-innoveraOrange-400">
          <WarningIcon className="size-6 text-innoveraOrange-400" weight="regular" />
        </div>
        <div className="flex flex-col items-center gap-1.5">
          <FigmaTypography variant="heading-1" className="text-center">
            {title}
          </FigmaTypography>
          <FigmaTypography variant="body-2" color="zinc500" className="text-center max-w-[420px]">
            {description}
          </FigmaTypography>
        </div>
        <Button
          type="button"
          data-testid="analysis-failed-retry-button"
          className="mt-2"
          disabled={isRetrying}
          onClick={onRetry}
        >
          {isRetrying ? (
            <Spinner data-icon="inline-start" aria-label={tCommon("loading")} />
          ) : (
            <ArrowClockwiseIcon className="size-4" weight="regular" />
          )}
          {t("generation.failedRetry")}
        </Button>
        {isLabs && (
          <FigmaTypography variant="label-2" color="zinc400" className="mt-2 text-center max-w-[520px] wrap-break-word">
            {error}
          </FigmaTypography>
        )}
      </div>
    </div>
  );
}
